import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';
import { from as observableFrom, Observable, of as observableOf, throwError as observableThrowError } from 'rxjs';
import { flatMap } from 'rxjs/operators';

import { serviceWorkerFetchStrategyHeader } from '../../service-worker/service-worker-fetch-strategy-header.constant';
import { HttpStatusCode } from '../../shared/constants/http-status-code.enum';
import { FetchStrategy } from '../../shared/instrumentation/instrumented-http.service';
import { HttpResponseContentType } from '../../shared/models/http-response-content-type.enum';
import { appEncryptionVersion, verifyEncryptionVersion } from '../../shared/utilities/encryption-version-verification.utility';
import { SERVICE_WORKER_ENABLED } from '../service-worker-enabled.injection-token';
import { ServiceWorkerInitiatedMessageService } from '../service-worker-initiated-message.service';
import { SERVICE_WORKER_USER_ID_FACTORY } from '../service-worker-user-id.injection-token';
import { DecryptResponseService } from './decrypt-response.service';
import { EncryptedResponse } from './encrypted-response.model';
import { EncryptionRequest, EncryptionResponse } from './encryption-message.model';
import { EncryptionService } from './encryption.service';

@Injectable()
export class EncryptionInterceptor implements HttpInterceptor {
    constructor(
        private readonly encryptionService: EncryptionService,
        private readonly serviceWorkerInitiatedMessageService: ServiceWorkerInitiatedMessageService,
        @Inject(SERVICE_WORKER_USER_ID_FACTORY) private readonly serviceWorkerUserIdFactory: () => Promise<string>,
        @Inject(SERVICE_WORKER_ENABLED) private readonly serviceWorkerEnabled: boolean,
        @Optional() private readonly decryptResponseService: DecryptResponseService
    ) {
        this.initialize();
    }

    public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // Only attempt to decrypt data from ServiceWorker Cache
        if (!this.decryptResponseService || request.headers.get(serviceWorkerFetchStrategyHeader) !== FetchStrategy.CacheOnly) {
            return next.handle(request);
        }

        // Assume all responses coming from Service Worker are encrypted ArrayBuffers
        const encryptedDataRequest = request.clone({ responseType: 'arraybuffer' });

        return next.handle(encryptedDataRequest).pipe(
            flatMap<HttpEvent<any>, HttpEvent<any>>(event => {
                if (event instanceof HttpResponse) {
                    const response: HttpResponse<ArrayBuffer> = event;

                    const responseBodyBytes = new Uint8Array(response.body);

                    if (!responseBodyBytes.length) {
                        return observableThrowError(
                            this.generateError(HttpStatusCode.UnexpectedResponse, 'Received an empty response from cache. Cannot decrypt.')
                        );
                    }

                    const isBinaryData = response.headers.get('Content-Type') === 'application/octet-stream';

                    // Check if this cached data is marked as binary; if not, refuse to read it as it cannot be trusted.
                    // This case should only happen between the transition from unencrypted worker caches to encrypted worker caches.
                    // After the encrypted worker activates, it will clear all old caches and all future data will be encrypted and
                    // appropriately marked as binary.
                    if (!isBinaryData) {
                        return observableThrowError(
                            this.generateError(HttpStatusCode.UnexpectedResponse, 'Data is not binary. Cannot decrypt.')
                        );
                    }

                    let ivAndEncryptedData: Uint8Array;

                    try {
                        ivAndEncryptedData = verifyEncryptionVersion(responseBodyBytes);
                    } catch (ex) {
                        return observableThrowError(
                            this.generateError(HttpStatusCode.DecryptionError, (ex as Error).message)
                        );
                    }

                    // Decrypt and replace in the response pipeline
                    return observableFrom(this.decryptResponseService.decryptResponse(response.url, ivAndEncryptedData, request.responseType as HttpResponseContentType));
                }

                return observableOf(event);
            })
        );
    }

    private async initialize() {
        if (!this.serviceWorkerEnabled) {
            return;
        }

        const serviceWorkerUserCacheId = await this.serviceWorkerUserIdFactory();

        this.serviceWorkerInitiatedMessageService.waitForTransactionalMessageAndRespond<EncryptionResponse>(messageEvent => {
            const message = messageEvent.data as EncryptionRequest;
            if (!message || message.type !== 'encrypt' || message.meta !== 'request') {
                return false;
            }

            const { userId, encryptionVersion } = message.payload;
            return userId === serviceWorkerUserCacheId && encryptionVersion === appEncryptionVersion;
        }, async messageEvent => {
            const message = messageEvent.data as EncryptionRequest;
            const { requestId, data } = message.payload;
            const encryptedData = await this.encryptionService.encryptData<EncryptedResponse>(data);
            return {
                type: 'encrypt',
                meta: 'response',
                payload: {
                    requestId,
                    encryptedData,
                    encryptionVersion: appEncryptionVersion
                }
            };
        });
    }

    private generateError(status: HttpStatusCode, statusText: string): HttpErrorResponse {
        return new HttpErrorResponse({
            error: statusText,
            status,
            statusText
        });
    }
}
